import { RolodexEntryType } from 'types'

// MAINTAINER-CONTRACT.IfChange:
// Registration lifecycle, stale-cleanup protection, refresh overlap selection,
// or refresh failure behavior changes.
// MAINTAINER-CONTRACT.ThenChange(src/features/rolodex/extensionFunctions/entriesWithSyncedRefreshes/README.md)
// MAINTAINER-CONTRACT.ThenChange(src/features/rolodex/extensionFunctions/entriesWithSyncedRefreshes/internal/providers/createRefreshSyncRegistry.test.ts)

export function createRefreshSyncRegistry(): RefreshSyncRegistry {
  const currentRegistrationByFetchId = new Map<
    SyncedRolodexFetchId,
    CurrentSyncedRolodexFetchRegistration
  >()

  const register = (
    syncedFetch: SyncedRolodexFetch
  ): UnregisterSyncedRolodexFetch => {
    const registrationOwnedByUnregister = {
      syncedFetch,
    }

    currentRegistrationByFetchId.set(
      syncedFetch.id,
      registrationOwnedByUnregister
    )

    return () => {
      const unregisterOwnsCurrentRegistration =
        currentRegistrationByFetchId.get(syncedFetch.id) ===
        registrationOwnedByUnregister

      if (!unregisterOwnsCurrentRegistration) {
        return
      }

      currentRegistrationByFetchId.delete(syncedFetch.id)
    }
  }

  const refreshOverlappingFetches = async (
    sourceFetchId: SyncedRolodexFetchId
  ): Promise<void> => {
    const sourceRegistration = currentRegistrationByFetchId.get(sourceFetchId)

    if (!sourceRegistration) {
      throw new Error('Cannot refresh unregistered synced Rolodex fetch')
    }

    const fetchesToRefresh = getFetchesToRefreshForSource({
      currentRegistrationByFetchId,
      sourceFetch: sourceRegistration.syncedFetch,
    })

    await Promise.all(
      fetchesToRefresh.map((syncedFetch) => syncedFetch.rawForceRefresh())
    )
  }

  return {
    register,
    refreshOverlappingFetches,
  }
}

// ----------------------------------------------------------------------------
//                                Helper Fns
// ----------------------------------------------------------------------------

function getFetchesToRefreshForSource({
  currentRegistrationByFetchId,
  sourceFetch,
}: GetFetchesToRefreshForSourceInput): ReadonlyArray<SyncedRolodexFetch> {
  return [
    sourceFetch,
    ...getFetchesOverlappingSource({
      currentRegistrationByFetchId,
      sourceFetch,
    }),
  ]
}

function getFetchesOverlappingSource({
  currentRegistrationByFetchId,
  sourceFetch,
}: GetFetchesToRefreshForSourceInput): ReadonlyArray<SyncedRolodexFetch> {
  if (sourceFetch.refreshSyncScope.size === 0) {
    return []
  }

  return Array.from(currentRegistrationByFetchId.values())
    .map((registration) => registration.syncedFetch)
    .filter((syncedFetch) => syncedFetch.id !== sourceFetch.id)
    .filter((syncedFetch) =>
      rolodexTypeSetsOverlap(
        sourceFetch.refreshSyncScope,
        syncedFetch.refreshSyncScope
      )
    )
}

function rolodexTypeSetsOverlap(
  firstRolodexTypeSet: ReadonlySet<RolodexEntryType>,
  secondRolodexTypeSet: ReadonlySet<RolodexEntryType>
): boolean {
  for (const rolodexType of firstRolodexTypeSet) {
    if (secondRolodexTypeSet.has(rolodexType)) {
      return true
    }
  }

  return false
}

// ----------------------------------------------------------------------------
//                                Helper Types
// ----------------------------------------------------------------------------

export interface RefreshSyncRegistry {
  register: (syncedFetch: SyncedRolodexFetch) => UnregisterSyncedRolodexFetch
  refreshOverlappingFetches: (
    sourceFetchId: SyncedRolodexFetchId
  ) => Promise<void>
}

export interface SyncedRolodexFetch {
  id: SyncedRolodexFetchId
  refreshSyncScope: ReadonlySet<RolodexEntryType>
  rawForceRefresh: RawRolodexFetchRefresh
}

export type SyncedRolodexFetchId = symbol

export type RawRolodexFetchRefresh = () => Promise<void>

export type UnregisterSyncedRolodexFetch = () => void

interface CurrentSyncedRolodexFetchRegistration {
  syncedFetch: SyncedRolodexFetch
}

interface GetFetchesToRefreshForSourceInput {
  currentRegistrationByFetchId: ReadonlyMap<
    SyncedRolodexFetchId,
    CurrentSyncedRolodexFetchRegistration
  >
  sourceFetch: SyncedRolodexFetch
}
